import { ReactNode } from 'react'

import { useNavigate } from 'react-router-dom'
import { LanguagesEnum } from 'common/enums/languages'
import { normalizeLanguage } from 'utils'
import { useAppInfo } from 'hooks/useAppInfo'

import { FlexColumn, FlexRow } from '@interco/inter-ui/components/Flex'
import { Signal, Text, Button } from '@interco/inter-toranja'
import { Spacing } from '@interco/inter-ui/components/Spacing'
import {
  TextSize,
  TextType,
  TextWeight,
} from '@interco/inter-toranja/dist/components/Atoms/Text/types'
import { interWbNavigate } from '@interco/inter-webview-bridge'
import IcHouse from '@interco/icons/core/action-navigation/ic_house'

import { feedbackPageData } from './constants'
import { FeedbackPageFlow, FeedbackPageType, feedbackRoutes } from './types'

type FeedbackPageProps = {
  children?: ReactNode
  feedbackType: FeedbackPageType
  flow: FeedbackPageFlow
  account?: string
  hasGoBackHome?: boolean
  onBeforeGoBackHome?: () => void
  onBeforeButtonClick?: () => void
}

export const FeedbackPage = ({
  children,
  feedbackType,
  flow,
  account,
  hasGoBackHome,
  onBeforeGoBackHome,
  onBeforeButtonClick,
}: FeedbackPageProps) => {
  const appInfo = useAppInfo()
  const normalizedLanguage = normalizeLanguage(appInfo?.language, LanguagesEnum.ES_ES)

  const navigate = useNavigate()

  const content = flow && feedbackPageData[normalizedLanguage]?.[flow]?.[feedbackType]

  const handleButtonClick = () => {
    if (!flow) return

    const { type, route } = feedbackRoutes[flow]

    onBeforeButtonClick?.()
    switch (type) {
      case 'redirect':
        navigate(account ? `${route}?account=${account}` : route)
        break
      case 'deeplink':
        interWbNavigate.openDeepLink(route)
        break
      case 'goback':
        interWbNavigate.requestGoBack()
        break
      default:
        break
    }
  }

  const handleGoBackHome = () => {
    onBeforeGoBackHome?.()
    interWbNavigate.openDeepLink('bancointer://home?refresh=true')
  }

  return (
    <FlexColumn height="100vh">
      {hasGoBackHome && (
        <FlexRow justifyContent="flex-end" className="px-6 pt-4">
          <IcHouse
            width={24}
            height={24}
            color="var(--primary500)"
            onClick={handleGoBackHome}
            style={{ cursor: 'pointer' }}
          />
        </FlexRow>
      )}

      <FlexColumn alignItems="center" justifyContent="center" height="100%">
        <Signal
          variant={FeedbackPageType.ERROR === feedbackType ? 'error' : 'success'}
          size="large"
          state="enabled"
        />

        <Spacing mb="sm" />

        <div className="flex p-6 gap-4 justify-center items-center text-center">
          <Text
            textType={TextType.Title}
            textSize={TextSize.Large}
            textWeight={TextWeight.Bold}
            as="h1"
          >
            {content?.title}
          </Text>
        </div>

        {children}
      </FlexColumn>

      <FlexColumn className="p-6" gap="1rem">
        <Button
          hierarchy="primary"
          style={{ width: '100%' }}
          onClick={handleButtonClick}
          label={content?.primaryButton}
        />
      </FlexColumn>
    </FlexColumn>
  )
}
